import React, { useState, useCallback, useEffect } from "react";
import { useApi, apiPost } from "../hooks/useApi.ts";
import { SearchSelect } from "./SearchSelect.tsx";
import { LoadingOverlay } from "./LoadingPanel.tsx";

interface Project {
  id: number;
  name: string;
}

interface Recording {
  id: string;
  title: string | null;
  started_at: string;
  processed: boolean;
  taskGroupId: number | null;
  taskGroupStatus: string | null;
}

interface RecordingsResponse {
  recordings: Recording[];
  cursor: string | null;
  hasMore: boolean;
}

interface ProcessResult {
  taskGroupId: number;
  parsed: {
    meetingSummary: string;
    tasks: Array<{
      name: string;
      description: string;
      departments: string[];
      priority: string;
    }>;
    followUpItems: string[];
  };
}

export function TranscriptList() {
  const { data: page, loading, error, refetch } = useApi<RecordingsResponse>("/api/transcripts");
  const { data: projects } = useApi<Project[]>("/api/projects");
  const [selectedProject, setSelectedProject] = useState<number>(0);
  const [processing, setProcessing] = useState<string | null>(null);
  const [result, setResult] = useState<ProcessResult | null>(null);
  const [allRecordings, setAllRecordings] = useState<Recording[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Sync page data from useApi into our accumulated state.
  // Runs on initial load and whenever `page` changes (e.g. after refetch following process/reprocess).
  useEffect(() => {
    if (page) {
      setAllRecordings(page.recordings);
      setCursor(page.cursor);
      setHasMore(page.hasMore);
    }
  }, [page]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetch(`/api/transcripts?cursor=${encodeURIComponent(cursor)}`);
      if (!res.ok) throw new Error(await res.text());
      const data = (await res.json()) as RecordingsResponse;
      setAllRecordings((prev) => [...prev, ...data.recordings]);
      setCursor(data.cursor);
      setHasMore(data.hasMore);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore]);

  const recordings = allRecordings;

  const handleProcess = async (id: string) => {
    if (!selectedProject) {
      alert("Please select a project first");
      return;
    }
    setProcessing(id);
    setResult(null);
    try {
      const data = await apiPost<ProcessResult>(`/api/transcripts/${id}/process`, {
        projectId: selectedProject,
      });
      setResult(data);
      refetch();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReprocess = async (id: string) => {
    if (!confirm("Re-process this transcript? The current draft tasks will be replaced.")) {
      return;
    }
    setProcessing(id);
    setResult(null);
    try {
      const data = await apiPost<ProcessResult>(`/api/transcripts/${id}/reprocess`, {});
      setResult(data);
      refetch();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) return <div className="loading">Loading transcripts...</div>;

  if (error) {
    let errorMessage = "An unexpected error occurred. Please try again.";
    try {
      const parsed = JSON.parse(error);
      if (parsed.error === "api_key_missing") {
        return (
          <div>
            <div className="page-header">
              <h1>Meeting Transcripts</h1>
              <p>Fellow recordings and AI-processed transcripts</p>
            </div>
            <div className="card">
              <div className="empty-state">
                <p>Fellow API key not configured.</p>
                <p style={{ marginTop: "8px" }}>
                  Go to <a href="#settings" style={{ color: "var(--color-primary)", textDecoration: "underline" }}>Settings</a> to add your Fellow API key.
                </p>
              </div>
            </div>
          </div>
        );
      }
      errorMessage = parsed.error || parsed.detail || errorMessage;
    } catch {}
    return (
      <div>
        <div className="page-header">
          <h1>Meeting Transcripts</h1>
          <p>Fellow recordings and AI-processed transcripts</p>
        </div>
        <div className="card">
          <div className="empty-state">
            <p style={{ color: "var(--color-danger)" }}>{errorMessage}</p>
            <p style={{ marginTop: "8px" }}>
              <button className="btn btn-sm btn-primary" onClick={refetch}>Retry</button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Meeting Transcripts</h1>
        <p>Fellow recordings and AI-processed transcripts</p>
      </div>

      <div className="card">
        <div className="form-group" style={{ marginBottom: "16px" }}>
          <label>Project</label>
          <SearchSelect
            options={projects?.map((p) => ({ value: p.id, label: p.name })) || []}
            value={selectedProject}
            onChange={setSelectedProject}
            placeholder="-- Select Project --"
          />
        </div>

        {recordings.length > 0 ? (
          <>
            <table>
              <thead>
                <tr>
                  <th>Meeting</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recordings.map((rec) => (
                  <tr key={rec.id}>
                    <td>{rec.title || "Untitled Meeting"}</td>
                    <td>{new Date(rec.started_at).toLocaleString()}</td>
                    <td>
                      <span className={`badge ${rec.processed ? "badge-success" : "badge-neutral"}`}>
                        {rec.processed ? "Processed" : "New"}
                      </span>
                    </td>
                    <td>
                      {!rec.processed && (
                        <button
                          className="btn btn-sm btn-primary"
                          disabled={processing === rec.id || !selectedProject}
                          onClick={() => handleProcess(rec.id)}
                        >
                          {processing === rec.id ? "Processing..." : "Process"}
                        </button>
                      )}
                      {rec.processed && (rec.taskGroupStatus === "draft" || rec.taskGroupStatus === "pending_review") && (
                        <button
                          className="btn btn-sm btn-secondary"
                          disabled={processing === rec.id}
                          onClick={() => handleReprocess(rec.id)}
                        >
                          {processing === rec.id ? "Re-processing..." : "Re-process"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div style={{ textAlign: "center", marginTop: "16px" }}>
                <button className="btn btn-secondary btn-sm" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            No recordings found. Ensure Fellow API is configured in Settings.
          </div>
        )}
      </div>

      {result && (
        <div className="card">
          <div className="card-header">
            <h2>Processing Result</h2>
          </div>
          <p style={{ marginBottom: "12px" }}>{result.parsed.meetingSummary}</p>
          <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
            Tasks ({result.parsed.tasks.length})
          </h3>
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Departments</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {result.parsed.tasks.map((task, i) => (
                <tr key={i}>
                  <td>
                    <strong>{task.name}</strong>
                    <br />
                    <span style={{ color: "var(--color-text-muted)", fontSize: "13px" }}>
                      {task.description.slice(0, 80)}...
                    </span>
                  </td>
                  <td>
                    {task.departments.map((d) => (
                      <span key={d} className="badge badge-neutral" style={{ marginRight: "4px" }}>
                        {d}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span className={`badge ${
                      task.priority === "high" ? "badge-danger" :
                      task.priority === "medium" ? "badge-warning" : "badge-neutral"
                    }`}>
                      {task.priority}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {result.parsed.followUpItems.length > 0 && (
            <>
              <h3 style={{ fontSize: "14px", fontWeight: 600, margin: "16px 0 8px" }}>
                Follow-up Items
              </h3>
              <ul style={{ paddingLeft: "20px" }}>
                {result.parsed.followUpItems.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {processing && (
        <LoadingOverlay message="Analyzing transcript with AI... This may take a moment." />
      )}
    </div>
  );
}
